import { LDProvider, initialize } from 'launchdarkly-react-client-sdk';
import type {
  LDClient,
  LDMultiKindContext,
} from 'launchdarkly-react-client-sdk';
import type { ReactNode } from 'react';

import { config } from '@/constants/config';
import {
  ANONYMOUS_CONTEXT_KEY,
  IDENTIFY_GUEST_CONTEXTS_FLAG,
  INITIALIZATION_TIMEOUT_SECONDS,
} from '@/constants/launchDarkly';
import type { LdIdentityAdapter } from '@/providers/launchDarklyIdentity';
import {
  LaunchDarklyIdentityContext,
  useGuestLdIdentity,
} from '@/providers/launchDarklyIdentity';
import {
  buildLdContext,
  flattenLdContext,
} from '@/utils/launchDarklyUtils.web';
import { Logger } from '@/utils/logging';
import { setLaunchDarklyClient } from '@/utils/tracking/trackers/launchDarklyTracker';

export {
  useIsLaunchDarklyIdentified,
  useLaunchDarklyIdentity,
} from '@/providers/launchDarklyIdentity';

// Conditionally create the client only if client-side ID is configured
let ldClient: LDClient | null = null;

if (config.launchDarkly.clientSideId) {
  ldClient = initialize(
    config.launchDarkly.clientSideId,
    {
      kind: 'user',
      key: ANONYMOUS_CONTEXT_KEY,
      anonymous: true,
    },
    {
      bootstrap: 'localStorage',
      sendEventsOnlyForVariation: true,
      wrapperName: 'react-client-sdk',
      wrapperVersion: '3.9.0',
    },
  );
  setLaunchDarklyClient(ldClient);
} else {
  Logger.error('LaunchDarkly client-side ID not configured for web');
  if (__DEV__) {
    throw new Error('LaunchDarkly client-side ID not configured for web');
  }
}

export { ldClient };

const webAdapter: LdIdentityAdapter = {
  readIdentifyGuestsFlag: async () => {
    try {
      await ldClient?.waitForInitialization(INITIALIZATION_TIMEOUT_SECONDS);
    } catch (error) {
      Logger.warn(
        '[LaunchDarkly Web] Guest identity flag unavailable; collapsing guests',
        error instanceof Error
          ? { message: error.message, name: error.name }
          : { error: String(error) },
      );

      return false;
    }

    return ldClient?.variation(IDENTIFY_GUEST_CONTEXTS_FLAG, false) === true;
  },

  // Undefined while the client still holds the single-kind context
  // `initialize()` bootstrapped, so the hook normalizes it to the multi-kind
  // shape every other flag targets before trusting the key.
  currentUserKey: () => {
    const context = ldClient?.getContext() as LDMultiKindContext | null;

    if (context?.kind !== 'multi') return undefined;

    return flattenLdContext(context, ANONYMOUS_CONTEXT_KEY).key;
  },

  identifyGuest: async (identity) => {
    await ldClient?.identify(
      buildLdContext({
        kind: 'user',
        key: identity.key,
        anonymousId: identity.anonymousId,
        anonymous: true,
      }),
    );
    Logger.console('LaunchDarkly context identified (Web)', { identity });
  },
};

interface LaunchDarklyProviderProps {
  children: ReactNode;
}

export const LaunchDarklyProvider = ({
  children,
}: LaunchDarklyProviderProps) => {
  const identityState = useGuestLdIdentity(ldClient ? webAdapter : null);

  // If no client (production without ID), return no-op provider
  if (!ldClient) {
    Logger.warn('[LaunchDarkly Web] No client available');
    return <>{children}</>;
  }

  return (
    <LaunchDarklyIdentityContext.Provider value={identityState}>
      <LDProvider
        clientSideID={config.launchDarkly.clientSideId}
        ldClient={ldClient}
      >
        {children}
      </LDProvider>
    </LaunchDarklyIdentityContext.Provider>
  );
};
